import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { runInAction, reaction } from "mobx";
import { useParams, useSearchParams } from "react-router-dom";

import { FilterValueType } from "../config/structured-search-schema";
import { StructuredSearchField, type Filter } from "../config/types";
import {
  type CompleteDateRange,
  isCompleteDateRange,
  StructuredSearchStore,
} from "../stores/structured-search/structured-search-store";

import {
  deserializeStructuredSearchFilters,
  serializeStructuredSearchFilters,
} from "./structuredSearchUrlFilters";
import {
  deleteMigratedParams,
  getStructuredSearchUrlNamespace,
  hasNamespacedParam,
  hasParamsToMigrate,
  readNamespacedParam,
  REQUESTS_TABLE_URL_NAMESPACE,
  supportsLegacyRequestFilterParams,
  type StructuredSearchUrlNamespace,
  type StructuredSearchUrlNamespaceId,
} from "./structuredSearchUrlNamespaces";

import {
  type DateRangeSearchParamNamespace,
  setDateRangeSearchParams,
  URL_PARAM_KEYS,
} from "@/utils/utils";
import { parsePositiveInt, REQUEST_LOGS_DEFAULT_PAGE_SIZE } from "@/utils/requestLogUrlParams";
import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";

interface LegacyMetadataFilterParam {
  key: string;
  value?: unknown;
  values?: unknown[];
}

interface LegacyPromptTemplateParam {
  name: string;
  version_numbers?: number[];
}

interface ResolvedLegacyPromptTemplate {
  promptId: number;
  promptName: string;
  versionNumbers?: number[];
}

const LEGACY_PROMPT_TEMPLATE_PARAM = "prompt_template";
const LEGACY_METADATA_OR_PARAM = "metadata_or";
const LEGACY_TAGS_OR_PARAM = "tags_or";
const LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM = "prompt_templates_include[]";
const LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM = "prompt_templates_exclude[]";
const LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM_UNBRACKETED = "prompt_templates_include";
const LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM_UNBRACKETED = "prompt_templates_exclude";

const resolveUrlNamespace = (
  namespace?: StructuredSearchUrlNamespace | StructuredSearchUrlNamespaceId,
): StructuredSearchUrlNamespace => {
  if (!namespace) {
    return REQUESTS_TABLE_URL_NAMESPACE;
  }
  if (typeof namespace === "string") {
    return getStructuredSearchUrlNamespace(namespace);
  }
  return namespace;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseLooseJsonObjectParam = (param: string): unknown => {
  try {
    return JSON.parse(param);
  } catch {
    try {
      const decoded = decodeURIComponent(param);
      if (decoded !== param) {
        return JSON.parse(decoded);
      }
    } catch {
      return null;
    }
  }

  return null;
};

const parseLegacyMetadataParam = (param: string): LegacyMetadataFilterParam[] => {
  const parsed = parseLooseJsonObjectParam(param);
  const entries = Array.isArray(parsed) ? parsed : [parsed];

  return entries.filter((entry): entry is LegacyMetadataFilterParam => {
    if (!isRecord(entry) || typeof entry.key !== "string") {
      return false;
    }

    return entry.value !== undefined || Array.isArray(entry.values);
  });
};

const parseLegacyStringListParams = (params: string[]): string[] =>
  params.flatMap(param => {
    const trimmed = param.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(item => String(item).trim()).filter(Boolean);
        }
      } catch {
        return [];
      }
    }

    return [trimmed];
  });

const normalizeLegacyPromptTemplateParam = (param: string): unknown => {
  const parsed = parseLooseJsonObjectParam(param);
  if (parsed) {
    return parsed;
  }

  const nameMatch = param.match(/[{,]\s*"?name"?\s*:\s*"?([^",}]+)"?/);
  if (!nameMatch?.[1]) {
    return null;
  }

  const versionsMatch = param.match(/"?version_numbers"?\s*:\s*\[([^\]]*)\]/);
  const versionNumbers = versionsMatch?.[1]
    ?.split(",")
    .map(item => Number(item.trim().replaceAll('"', "")))
    .filter(item => Number.isInteger(item) && item > 0);

  return {
    name: nameMatch[1],
    version_numbers: versionNumbers && versionNumbers.length > 0 ? versionNumbers : undefined,
  };
};

const readLegacyPromptTemplateParamValue = (param: string): LegacyPromptTemplateParam | null => {
  if (!param) {
    return null;
  }

  try {
    const parsed = normalizeLegacyPromptTemplateParam(param);
    if (!isRecord(parsed) || typeof parsed.name !== "string") {
      return null;
    }

    const name = parsed.name.trim();
    if (!name) {
      return null;
    }

    const versionNumbers = Array.isArray(parsed.version_numbers)
      ? parsed.version_numbers
          .map(item => Number(item))
          .filter(item => Number.isInteger(item) && item > 0)
      : undefined;

    return {
      name,
      version_numbers: versionNumbers && versionNumbers.length > 0 ? versionNumbers : undefined,
    };
  } catch {
    return null;
  }
};

export const readLegacyPromptTemplateParam = (
  searchParams: URLSearchParams,
): LegacyPromptTemplateParam | null => {
  const param =
    searchParams.get(URL_PARAM_KEYS.PROMPT_TEMPLATE) ??
    searchParams.get(LEGACY_PROMPT_TEMPLATE_PARAM);
  return param ? readLegacyPromptTemplateParamValue(param) : null;
};

const readLegacyPromptTemplateListParams = (
  searchParams: URLSearchParams,
  paramNames: string[],
): LegacyPromptTemplateParam[] =>
  paramNames
    .flatMap(paramName => searchParams.getAll(paramName))
    .map(readLegacyPromptTemplateParamValue)
    .filter(
      (promptTemplate): promptTemplate is LegacyPromptTemplateParam => promptTemplate !== null,
    );

const readLegacyPromptTemplates = (
  searchParams: URLSearchParams,
): {
  include: LegacyPromptTemplateParam[];
  exclude: LegacyPromptTemplateParam[];
} => {
  const singlePromptTemplate = readLegacyPromptTemplateParam(searchParams);
  return {
    include: [
      ...(singlePromptTemplate ? [singlePromptTemplate] : []),
      ...readLegacyPromptTemplateListParams(searchParams, [
        LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM,
        LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM_UNBRACKETED,
      ]),
    ],
    exclude: readLegacyPromptTemplateListParams(searchParams, [
      LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM,
      LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM_UNBRACKETED,
    ]),
  };
};

export const deserializeLegacyMetadataFilters = (searchParams: URLSearchParams): Filter[] =>
  Array.from(
    [URL_PARAM_KEYS.METADATA, LEGACY_METADATA_OR_PARAM]
      .flatMap(paramKey => searchParams.getAll(paramKey))
      .flatMap(parseLegacyMetadataParam)
      .reduce<Map<string, string[]>>((metadataValuesByKey, legacyFilter) => {
        const { key, value, values } = legacyFilter;
        const nestedKey = key.trim();
        if (!nestedKey) {
          return metadataValuesByKey;
        }

        const rawValues = values ?? (value !== undefined ? [value] : []);
        const filterValues = rawValues.map(item => String(item).trim()).filter(Boolean);

        if (filterValues.length === 0) {
          return metadataValuesByKey;
        }

        const currentValues = metadataValuesByKey.get(nestedKey) ?? [];
        metadataValuesByKey.set(nestedKey, [...currentValues, ...filterValues]);
        return metadataValuesByKey;
      }, new Map<string, string[]>()),
  ).flatMap(([nestedKey, filterValues]): Filter[] => {
    if (filterValues.length === 1) {
      return [
        {
          id: crypto.randomUUID(),
          field: StructuredSearchField.Metadata,
          type: FilterValueType.Nested,
          operator: "key_equals",
          value: filterValues[0],
          valueDisplay: filterValues[0],
          nestedKey,
        },
      ];
    }

    return [
      {
        id: crypto.randomUUID(),
        field: StructuredSearchField.Metadata,
        type: FilterValueType.Nested,
        operator: "in",
        value: filterValues,
        multiValueDisplay: filterValues,
        nestedKey,
      },
    ];
  });

const deserializeLegacyTagFilters = (searchParams: URLSearchParams): Filter[] => {
  const tags = parseLegacyStringListParams([
    ...searchParams.getAll(URL_PARAM_KEYS.TAGS),
    ...searchParams.getAll(LEGACY_TAGS_OR_PARAM),
  ]);
  if (tags.length === 0) {
    return [];
  }

  return [
    {
      id: crypto.randomUUID(),
      field: StructuredSearchField.Tags,
      type: FilterValueType.String,
      operator: tags.length === 1 ? "contains" : "in",
      value: tags.length === 1 ? tags[0] : tags,
      multiValueDisplay: tags.length === 1 ? undefined : tags,
    },
  ];
};

const getResolvedPromptFilterValues = (promptTemplates: ResolvedLegacyPromptTemplate[]) =>
  promptTemplates.flatMap(promptTemplate => {
    const versionNumbers = promptTemplate.versionNumbers ?? [];
    if (versionNumbers.length === 0) {
      return [
        {
          value: { prompt_id: promptTemplate.promptId },
          display: `${promptTemplate.promptName} (Any version)`,
        },
      ];
    }

    return versionNumbers.map(versionNumber => ({
      value: {
        prompt_id: promptTemplate.promptId,
        prompt_version_number: versionNumber,
      },
      display: `${promptTemplate.promptName} v${versionNumber}`,
    }));
  });

const deserializeLegacyPromptTemplateFilters = ({
  include = [],
  exclude = [],
}: {
  include?: ResolvedLegacyPromptTemplate[];
  exclude?: ResolvedLegacyPromptTemplate[];
} = {}): Filter[] => {
  const includeValues = getResolvedPromptFilterValues(include);
  const excludeValues = getResolvedPromptFilterValues(exclude);
  const filters: Filter[] = [];

  if (includeValues.length === 1) {
    filters.push({
      id: crypto.randomUUID(),
      field: "prompt_id",
      type: FilterValueType.Prompt,
      operator: "is",
      value: includeValues[0].value,
      valueDisplay: includeValues[0].display,
    });
  } else if (includeValues.length > 1) {
    filters.push({
      id: crypto.randomUUID(),
      field: "prompt_id",
      type: FilterValueType.Prompt,
      operator: "in",
      value: includeValues.map(item => item.value),
      multiValueDisplay: includeValues.map(item => item.display),
    });
  }

  if (excludeValues.length > 0) {
    filters.push({
      id: crypto.randomUUID(),
      field: "prompt_id",
      type: FilterValueType.Prompt,
      operator: "not_in",
      value: excludeValues.map(item => item.value),
      multiValueDisplay: excludeValues.map(item => item.display),
    });
  }

  return filters;
};

export const deserializeLegacyStructuredFilters = (
  searchParams: URLSearchParams,
  options: {
    promptTemplates?: {
      include?: ResolvedLegacyPromptTemplate[];
      exclude?: ResolvedLegacyPromptTemplate[];
    };
  } = {},
): Filter[] => [
  ...deserializeLegacyMetadataFilters(searchParams),
  ...deserializeLegacyTagFilters(searchParams),
  ...deserializeLegacyPromptTemplateFilters(options.promptTemplates),
];

const findResolvedLegacyPromptTemplate = (
  legacyPromptTemplate: LegacyPromptTemplateParam | null,
  promptIdValue: unknown,
): ResolvedLegacyPromptTemplate | null => {
  const promptId = Number(promptIdValue);
  if (!legacyPromptTemplate || !Number.isInteger(promptId) || promptId <= 0) {
    return null;
  }

  return {
    promptId,
    promptName: legacyPromptTemplate.name,
    versionNumbers: legacyPromptTemplate.version_numbers,
  };
};

const useLegacyPromptTemplatesLookup = (
  legacyPromptTemplates: LegacyPromptTemplateParam[],
  enabled: boolean,
) => {
  const userToken = useUserToken();
  const { workspaceId: routeWorkspaceId } = useParams();
  const workspaceId = routeWorkspaceId ? Number(routeWorkspaceId) : undefined;
  const serializedPromptTemplates = JSON.stringify(legacyPromptTemplates);

  return useQuery<ResolvedLegacyPromptTemplate[]>({
    queryKey: ["legacyPromptTemplatesLookup", workspaceId, serializedPromptTemplates, userToken],
    queryFn: async () => {
      if (!userToken || !workspaceId || legacyPromptTemplates.length === 0) {
        return [];
      }

      const resolvedPromptTemplates = await Promise.all(
        legacyPromptTemplates.map(async legacyPromptTemplate => {
          const response = await fetch(ENDPOINTS.get_prompt, {
            method: "POST",
            headers: jsonAuthHeaders(userToken),
            body: JSON.stringify({
              workspace_id: workspaceId,
              prompt_name: legacyPromptTemplate.name,
            }),
          });

          if (!response.ok) {
            return null;
          }

          const data = (await response.json()) as { prompt_id?: unknown };
          return findResolvedLegacyPromptTemplate(legacyPromptTemplate, data.prompt_id);
        }),
      );

      return resolvedPromptTemplates.filter(
        (promptTemplate): promptTemplate is ResolvedLegacyPromptTemplate => promptTemplate !== null,
      );
    },
    enabled: enabled && !!userToken && !!workspaceId && legacyPromptTemplates.length > 0,
    retry: false,
    staleTime: 60 * 1000,
  });
};

const areFiltersEquivalent = (left: Filter[], right: Filter[]): boolean =>
  serializeStructuredSearchFilters(left) === serializeStructuredSearchFilters(right);

const readPaginationFromSearchParams = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
) => ({
  pageIndex: parsePositiveInt(readNamespacedParam(searchParams, namespace, "page"), 1),
  pageSize: parsePositiveInt(
    readNamespacedParam(searchParams, namespace, "pageSize"),
    REQUEST_LOGS_DEFAULT_PAGE_SIZE,
  ),
});

const readSortingFromSearchParams = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
) => {
  const sortBy = readNamespacedParam(searchParams, namespace, "sortBy");
  return sortBy
    ? [{ id: sortBy, desc: readNamespacedParam(searchParams, namespace, "sortDesc") === "true" }]
    : [];
};

const readNamespacedDateRange = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
): { from: Date; to: Date } | undefined => {
  const startParam = readNamespacedParam(searchParams, namespace, "startTime");
  const endParam = readNamespacedParam(searchParams, namespace, "endTime");
  if (!startParam || !endParam) {
    return undefined;
  }
  return { from: new Date(startParam), to: new Date(endParam) };
};

const setNamespacedDateRange = (
  params: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
  dateRange: CompleteDateRange,
): void => {
  params.set(namespace.startTime, dateRange.from.toISOString());
  params.set(namespace.endTime, dateRange.to.toISOString());
};

const buildStructuredSearchApplySnapshot = (
  searchParams: URLSearchParams,
  store: StructuredSearchStore,
  namespace: StructuredSearchUrlNamespace,
) => {
  const { pageIndex: urlPageIndex, pageSize: urlPageSize } = readPaginationFromSearchParams(
    searchParams,
    namespace,
  );
  const urlSorting = readSortingFromSearchParams(searchParams, namespace);

  return {
    filters: store.editableFilters,
    dateRange: store.dateRange,
    q: store.q,
    pageIndex: hasNamespacedParam(searchParams, namespace, "page") ? urlPageIndex : store.pageIndex,
    pageSize: hasNamespacedParam(searchParams, namespace, "pageSize")
      ? urlPageSize
      : store.pageSize,
    sorting: hasNamespacedParam(searchParams, namespace, "sortBy") ? urlSorting : store.sorting,
  };
};

const shouldRepairMissingStructuredSearchParams = (
  searchParams: URLSearchParams,
  store: StructuredSearchStore,
  namespace: StructuredSearchUrlNamespace,
): boolean => {
  if (hasParamsToMigrate(searchParams, namespace)) {
    return true;
  }

  const serializedFilters = serializeStructuredSearchFilters(store.editableFilters);
  if (!hasNamespacedParam(searchParams, namespace, "filters") && serializedFilters !== "[]") {
    return true;
  }
  if (!hasNamespacedParam(searchParams, namespace, "q") && store.q !== "") {
    return true;
  }
  if (!hasNamespacedParam(searchParams, namespace, "page") && store.pageIndex !== 1) {
    return true;
  }
  if (
    !hasNamespacedParam(searchParams, namespace, "pageSize") &&
    store.pageSize !== REQUEST_LOGS_DEFAULT_PAGE_SIZE
  ) {
    return true;
  }
  if (!hasNamespacedParam(searchParams, namespace, "sortBy") && store.sorting.length > 0) {
    return true;
  }
  return false;
};

const isStructuredSearchStateInSearchParams = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
  filters: Filter[],
  dateRange: CompleteDateRange,
): boolean => {
  const serializedFilters = serializeStructuredSearchFilters(filters);
  const filtersParam = readNamespacedParam(searchParams, namespace, "filters");
  const filtersMatch =
    serializedFilters === "[]" ? !filtersParam : filtersParam === serializedFilters;

  const urlDateRange = readNamespacedDateRange(searchParams, namespace);
  const dateRangeMatches =
    !!urlDateRange &&
    urlDateRange.from.getTime() === dateRange.from.getTime() &&
    urlDateRange.to.getTime() === dateRange.to.getTime();

  return filtersMatch && dateRangeMatches;
};

export interface ApplyStructuredSearchStateOptions {
  resetPage?: boolean;
  /** @deprecated Prefer `namespace`. Kept for callers that only override date keys. */
  dateRangeNamespace?: DateRangeSearchParamNamespace;
  namespace?: StructuredSearchUrlNamespace | StructuredSearchUrlNamespaceId;
  /** When true, strip legacy metadata/tags/prompt params after converting to structured filters. */
  clearLegacyParams?: boolean;
}

export const applyStructuredSearchStateToSearchParams = (
  previousParams: URLSearchParams,
  {
    filters,
    dateRange,
    q,
    pageIndex,
    pageSize,
    sorting,
  }: {
    filters: Filter[];
    dateRange: CompleteDateRange;
    q?: string;
    pageIndex?: number;
    pageSize?: number;
    sorting?: { id: string; desc: boolean }[];
  },
  options: ApplyStructuredSearchStateOptions = {},
): URLSearchParams => {
  const namespace = resolveUrlNamespace(options.namespace);
  const params = new URLSearchParams(previousParams);
  const clearLegacyParams =
    options.clearLegacyParams ?? supportsLegacyRequestFilterParams(namespace);

  deleteMigratedParams(params, namespace);

  if (clearLegacyParams) {
    params.delete(URL_PARAM_KEYS.METADATA);
    params.delete(URL_PARAM_KEYS.TAGS);
    params.delete(URL_PARAM_KEYS.PROMPT_TEMPLATE);
    params.delete(LEGACY_METADATA_OR_PARAM);
    params.delete(LEGACY_TAGS_OR_PARAM);
    params.delete(LEGACY_PROMPT_TEMPLATE_PARAM);
    params.delete(LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM);
    params.delete(LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM);
    params.delete(LEGACY_PROMPT_TEMPLATES_INCLUDE_PARAM_UNBRACKETED);
    params.delete(LEGACY_PROMPT_TEMPLATES_EXCLUDE_PARAM_UNBRACKETED);
  }

  if (options.resetPage) {
    params.delete(namespace.page);
  }

  if (filters.length === 0) {
    params.delete(namespace.filters);
  } else {
    params.set(namespace.filters, serializeStructuredSearchFilters(filters));
  }

  if (q !== undefined) {
    if (q) {
      params.set(namespace.q, q);
    } else {
      params.delete(namespace.q);
    }
  }

  if (pageIndex !== undefined && !options.resetPage) {
    params.set(namespace.page, String(pageIndex));
  }

  if (pageSize !== undefined) {
    params.set(namespace.pageSize, String(pageSize));
  }

  if (sorting !== undefined) {
    const firstSort = sorting[0];
    if (firstSort) {
      params.set(namespace.sortBy, firstSort.id);
      params.set(namespace.sortDesc, String(firstSort.desc));
    } else {
      params.delete(namespace.sortBy);
      params.delete(namespace.sortDesc);
    }
  }

  // Prefer explicit namespaced date keys; fall back to legacy date-range helper when
  // callers only pass dateRangeNamespace (older call sites).
  if (options.dateRangeNamespace && options.namespace === undefined) {
    return setDateRangeSearchParams(params, options.dateRangeNamespace, dateRange);
  }

  setNamespacedDateRange(params, namespace, dateRange);
  return params;
};

export const useStructuredSearchURLSync = (
  store: StructuredSearchStore,
  enabled = true,
  namespaceOption?: StructuredSearchUrlNamespace | StructuredSearchUrlNamespaceId,
) => {
  const namespace = resolveUrlNamespace(namespaceOption);
  const [searchParams, setSearchParams] = useSearchParams();
  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  const isUpdatingFromURLRef = useRef(false);
  const legacyPromptTemplates = useMemo(
    () => readLegacyPromptTemplates(searchParams),
    [searchParams],
  );
  const shouldResolveLegacyPromptTemplates =
    enabled &&
    supportsLegacyRequestFilterParams(namespace) &&
    !hasNamespacedParam(searchParams, namespace, "filters") &&
    (legacyPromptTemplates.include.length > 0 || legacyPromptTemplates.exclude.length > 0);
  const legacyPromptTemplatesIncludeLookupQuery = useLegacyPromptTemplatesLookup(
    legacyPromptTemplates.include,
    shouldResolveLegacyPromptTemplates,
  );
  const legacyPromptTemplatesExcludeLookupQuery = useLegacyPromptTemplatesLookup(
    legacyPromptTemplates.exclude,
    shouldResolveLegacyPromptTemplates,
  );
  const resolvedLegacyPromptTemplates = useMemo(
    () => ({
      include: legacyPromptTemplatesIncludeLookupQuery.data ?? [],
      exclude: legacyPromptTemplatesExcludeLookupQuery.data ?? [],
    }),
    [legacyPromptTemplatesExcludeLookupQuery.data, legacyPromptTemplatesIncludeLookupQuery.data],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isUpdatingFromURLRef.current) {
      return;
    }
    if (
      shouldResolveLegacyPromptTemplates &&
      ((legacyPromptTemplates.include.length > 0 &&
        legacyPromptTemplatesIncludeLookupQuery.data === undefined) ||
        (legacyPromptTemplates.exclude.length > 0 &&
          legacyPromptTemplatesExcludeLookupQuery.data === undefined))
    ) {
      return;
    }

    let needsUpdate = false;
    let shouldRepairDateRange = false;
    const hasPendingStoreToUrlSync = updateTimeoutRef.current !== undefined;
    runInAction(() => {
      if (!hasPendingStoreToUrlSync) {
        if (hasNamespacedParam(searchParams, namespace, "filters")) {
          const param = readNamespacedParam(searchParams, namespace, "filters");
          const filters = param ? deserializeStructuredSearchFilters(param) : [];
          if (!areFiltersEquivalent(store.filters, filters)) {
            store.setFiltersFromURL(filters);
            needsUpdate = true;
          }
        } else if (
          supportsLegacyRequestFilterParams(namespace) &&
          (searchParams.has(URL_PARAM_KEYS.METADATA) ||
            searchParams.has(URL_PARAM_KEYS.TAGS) ||
            searchParams.has(LEGACY_METADATA_OR_PARAM) ||
            searchParams.has(LEGACY_TAGS_OR_PARAM) ||
            legacyPromptTemplates.include.length > 0 ||
            legacyPromptTemplates.exclude.length > 0)
        ) {
          const filters = deserializeLegacyStructuredFilters(searchParams, {
            promptTemplates: resolvedLegacyPromptTemplates,
          });
          if (filters.length > 0 && !areFiltersEquivalent(store.filters, filters)) {
            store.setFiltersFromURL(filters);
            needsUpdate = true;
          }
        } else if (store.filters.length > 0) {
          store.setFiltersFromURL([]);
          needsUpdate = true;
        }
      }

      if (hasNamespacedParam(searchParams, namespace, "q")) {
        const q = readNamespacedParam(searchParams, namespace, "q") ?? "";
        if (q !== store.q) {
          store.setSearch(q);
          needsUpdate = true;
        }
      } else if (store.q !== "") {
        store.setSearch("");
        needsUpdate = true;
      }

      if (hasNamespacedParam(searchParams, namespace, "page")) {
        const { pageIndex } = readPaginationFromSearchParams(searchParams, namespace);
        if (pageIndex !== store.pageIndex) {
          store.setPageIndex(pageIndex);
          needsUpdate = true;
        }
      }

      if (hasNamespacedParam(searchParams, namespace, "pageSize")) {
        const { pageSize } = readPaginationFromSearchParams(searchParams, namespace);
        if (pageSize !== store.pageSize) {
          store.setPageSize(pageSize);
          needsUpdate = true;
        }
      }

      if (hasNamespacedParam(searchParams, namespace, "sortBy")) {
        const sorting = readSortingFromSearchParams(searchParams, namespace);
        if (JSON.stringify(sorting) !== JSON.stringify(store.sorting)) {
          store.setSorting(sorting);
          needsUpdate = true;
        }
      }

      const dateRange = readNamespacedDateRange(searchParams, namespace);

      if (isCompleteDateRange(dateRange)) {
        store.setDateRange(dateRange);
        needsUpdate = true;
      } else {
        // URL is missing a date range — keep the store's current range (callers may have
        // seeded a domain-specific default, e.g. prompt version created_at) and repair the URL.
        shouldRepairDateRange = true;
      }
    });

    const shouldRepairStructuredSearchParams = shouldRepairMissingStructuredSearchParams(
      searchParams,
      store,
      namespace,
    );

    if (shouldRepairDateRange || shouldRepairStructuredSearchParams) {
      isUpdatingFromURLRef.current = true;
      setSearchParams(
        previousParams =>
          applyStructuredSearchStateToSearchParams(
            previousParams,
            buildStructuredSearchApplySnapshot(previousParams, store, namespace),
            { namespace },
          ),
        { replace: true },
      );
      setTimeout(() => {
        isUpdatingFromURLRef.current = false;
      }, 50);
      store.setUrlHydrationReady(true);
      return;
    }

    if (needsUpdate) {
      isUpdatingFromURLRef.current = true;
      setTimeout(() => {
        isUpdatingFromURLRef.current = false;
      }, 50);
    }

    store.setUrlHydrationReady(true);
  }, [
    enabled,
    legacyPromptTemplates,
    legacyPromptTemplatesExcludeLookupQuery.data,
    legacyPromptTemplatesIncludeLookupQuery.data,
    namespace,
    resolvedLegacyPromptTemplates,
    searchParams,
    setSearchParams,
    shouldResolveLegacyPromptTemplates,
    store,
  ]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const disposer = reaction(
      () => ({
        filters: store.editableFilters,
        dateRange: store.dateRange,
        q: store.q,
        pageIndex: store.pageIndex,
        pageSize: store.pageSize,
        sorting: store.sorting,
      }),
      ({ filters, dateRange, q, pageIndex, pageSize, sorting }) => {
        if (isUpdatingFromURLRef.current) {
          return;
        }

        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          updateTimeoutRef.current = undefined;
          isUpdatingFromURLRef.current = true;
          setSearchParams(
            prev => {
              const resetPage = !isStructuredSearchStateInSearchParams(
                prev,
                namespace,
                filters,
                dateRange,
              );
              return applyStructuredSearchStateToSearchParams(
                prev,
                { filters, dateRange, q, pageIndex, pageSize, sorting },
                { resetPage, namespace },
              );
            },
            {
              replace: true,
            },
          );
          setTimeout(() => {
            isUpdatingFromURLRef.current = false;
          }, 50);
        }, 150);
      },
      { fireImmediately: false },
    );

    return () => {
      disposer();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [enabled, namespace, store, setSearchParams]);

  useEffect(
    () => () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    },
    [],
  );
};
